import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTheme } from "@mui/material/styles";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, BarElement, LineElement, ArcElement,
  CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler,
} from "chart.js";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import { useGetSkinDashboardQuery } from "slices/addOnApi";

import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonIcon from "@mui/icons-material/Person";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import BarChartIcon from "@mui/icons-material/BarChart";

ChartJS.register(BarElement, LineElement, ArcElement, CategoryScale, LinearScale, PointElement, Tooltip, Legend, Filler);

// ── theme constants ───────────────────────────────────────────
const INDIGO = "#5c6bc0";
const INDIGO_LIGHT = "rgba(92,107,192,0.12)";

// ── helpers ───────────────────────────────────────────────────
const SC = ({ children }) => (
  <Typography variant="h6" fontWeight={700} mb={2}
    sx={{ color: "#1a237e", borderLeft: `4px solid ${INDIGO}`, pl: 1.5 }}>
    {children}
  </Typography>
);

const KpiCard = ({ icon, label, value, sub, subColor = "#22c55e", bg = "#fff" }) => (
  <Card sx={{ p: 2.5, borderRadius: 3, height: "100%", background: bg, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={600}
          textTransform="uppercase" letterSpacing={1}>{label}</Typography>
        <Typography variant="h4" fontWeight={700} mt={0.5}>{value ?? 0}</Typography>
        {sub && (
          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
            <TrendingUpIcon sx={{ fontSize: 13, color: subColor }} />
            <Typography variant="caption" fontWeight={600} sx={{ color: subColor }}>{sub}</Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ background: INDIGO_LIGHT, borderRadius: 2, p: 1.2, display: "flex" }}>{icon}</Box>
    </Box>
  </Card>
);

const payStatusColor = { success: "success", pending: "warning", faliour: "error", cancelled: "default" };
const PayChip = (s) => (
  <Chip label={s || "—"} size="small" color={payStatusColor[s] || "default"}
    sx={{ fontWeight: 600, fontSize: 11 }} />
);

const TH = ({ cols }) => (
  <TableHead>
    <TableRow sx={{ background: "#e8eaf6" }}>
      {cols.map(c => (
        <TableCell key={c} sx={{ fontWeight: 700, fontSize: 12, color: "#1a237e", whiteSpace: "nowrap", px: 2, py: 1 }}>
          {c}
        </TableCell>
      ))}
    </TableRow>
  </TableHead>
);

const chartOpts = (theme) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { labels: { color: theme.palette.text.primary, font: { size: 11 } } },
    tooltip: { mode: "index", intersect: false },
  },
  scales: {
    x: { ticks: { color: theme.palette.text.secondary, font: { size: 10 } }, grid: { color: theme.palette.divider } },
    y: { beginAtZero: true, ticks: { color: theme.palette.text.secondary, font: { size: 10 } }, grid: { color: theme.palette.divider } },
  },
});

const doughnutOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } },
};

// ── component ─────────────────────────────────────────────────
function SkinAnalysisDashboard() {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetSkinDashboardQuery();

  if (isLoading) return (
    <DashboardLayout><DashboardNavbar />
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: INDIGO }} />
      </Box>
      <Footer />
    </DashboardLayout>
  );

  if (isError || !data) return (
    <DashboardLayout><DashboardNavbar />
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">Failed to load skin analysis dashboard.</Typography>
      </Box>
      <Footer />
    </DashboardLayout>
  );

  const {
    skinReportStats, scanPayStats, monthlyScanEarnings,
    skinTypeDistribution, topScanConcerns, scanGenderBreakdown,
    scanPayStatusBreakdown, avgSkinScore, latestScanReports,
  } = data;

  // ── chart datasets ──────────────────────────────────────────
  const months = monthlyScanEarnings?.map(d => d.month) || [];

  const monthlyLineData = {
    labels: months,
    datasets: [
      {
        label: "Scan Payments",
        data: monthlyScanEarnings?.map(d => d.scanCount) || [],
        borderColor: INDIGO, backgroundColor: "rgba(92,107,192,0.15)",
        fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: INDIGO,
        yAxisID: "y",
      },
      {
        label: "Scan Reports",
        data: monthlyScanEarnings?.map(d => d.reportCount) || [],
        borderColor: "#26c6da", backgroundColor: "rgba(38,198,218,0.1)",
        fill: false, tension: 0.4, pointRadius: 4, pointBackgroundColor: "#26c6da",
        yAxisID: "y",
      },
    ],
  };

  const earningLineData = {
    labels: months,
    datasets: [{
      label: "Earnings (₹)",
      data: monthlyScanEarnings?.map(d => d.earnings) || [],
      borderColor: "#ef5350", backgroundColor: "rgba(239,83,80,0.12)",
      fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: "#ef5350",
    }],
  };

  const skinTypeBarData = {
    labels: skinTypeDistribution?.map(d => d._id || "Unknown") || [],
    datasets: [{
      label: "Reports",
      data: skinTypeDistribution?.map(d => d.count) || [],
      backgroundColor: ["rgba(92,107,192,0.8)","rgba(38,198,218,0.8)","rgba(239,83,80,0.8)","rgba(102,187,106,0.8)","rgba(255,167,38,0.8)"],
      borderRadius: 4,
    }],
  };

  const concernBarData = {
    labels: topScanConcerns?.map(d => d._id || "—") || [],
    datasets: [
      {
        label: "Occurrences",
        data: topScanConcerns?.map(d => d.count) || [],
        backgroundColor: "rgba(92,107,192,0.75)",
        borderRadius: 4,
        yAxisID: "y",
      },
      {
        label: "Avg %",
        data: topScanConcerns?.map(d => +d.avgPercentage.toFixed(1)) || [],
        backgroundColor: "rgba(239,83,80,0.6)",
        borderRadius: 4,
        yAxisID: "y1",
      },
    ],
  };

  const concernBarOpts = {
    ...chartOpts(theme),
    scales: {
      ...chartOpts(theme).scales,
      y1: {
        type: "linear", position: "right", beginAtZero: true,
        ticks: { color: "#ef5350", font: { size: 10 } },
        grid: { drawOnChartArea: false },
      },
    },
  };

  const payStatusDoughnutData = {
    labels: scanPayStatusBreakdown?.map(d => d._id || "—") || [],
    datasets: [{
      data: scanPayStatusBreakdown?.map(d => d.count) || [],
      backgroundColor: ["#66bb6a", "#ffa726", "#ef5350", "#bdbdbd"],
      borderWidth: 0,
    }],
  };

  const genderKeys = Object.keys(scanGenderBreakdown || {});
  const genderColors = ["#42a5f5", "#ec407a", "#ab47bc", "#26c6da"];
  const genderDoughnutData = {
    labels: genderKeys,
    datasets: [{
      data: genderKeys.map(k => scanGenderBreakdown[k]),
      backgroundColor: genderColors,
      borderWidth: 0,
    }],
  };

  const scanUsedDoughnutData = {
    labels: ["Scan Used", "Scan Unused"],
    datasets: [{
      data: [scanPayStats?.scanUsed || 0, scanPayStats?.scanUnused || 0],
      backgroundColor: [INDIGO, "#e0e0e0"],
      borderWidth: 0,
    }],
  };

  // max concern count for progress bar
  const maxConcernCount = Math.max(...(topScanConcerns?.map(c => c.count) || [1]), 1);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── KPI ROW 1 — Scan Reports ── */}
        <Grid container spacing={2.5} mb={3}>
          {[
            { icon: <FaceRetouchingNaturalIcon sx={{ color: INDIGO, fontSize: 26 }} />, label: "Total Scan Reports", value: skinReportStats?.total?.toLocaleString(), sub: `+${skinReportStats?.today || 0} today` },
            { icon: <PersonIcon sx={{ color: "#26c6da", fontSize: 26 }} />, label: "User Scans", value: skinReportStats?.userScans?.toLocaleString(), bg: "#e8eaf6" },
            { icon: <PersonIcon sx={{ color: "#94a3b8", fontSize: 26 }} />, label: "Guest Scans", value: skinReportStats?.guestScans?.toLocaleString(), bg: "#f8fafc", subColor: "#94a3b8" },
            { icon: <ImageSearchIcon sx={{ color: "#66bb6a", fontSize: 26 }} />, label: "Scans with Image", value: skinReportStats?.withImage?.toLocaleString(), bg: "#f0fdf4" },
            { icon: <BarChartIcon sx={{ color: INDIGO, fontSize: 26 }} />, label: "Avg Skin Score", value: avgSkinScore?.avg ? avgSkinScore.avg.toFixed(1) : "—", sub: `Min ${avgSkinScore?.min || 0} · Max ${avgSkinScore?.max || 0}` },
            { icon: <QrCodeScannerIcon sx={{ color: INDIGO, fontSize: 26 }} />, label: "Total Scan Payments", value: scanPayStats?.total?.toLocaleString(), sub: `+${scanPayStats?.today || 0} today` },
            { icon: <CheckCircleIcon sx={{ color: "#66bb6a", fontSize: 26 }} />, label: "Successful Payments", value: scanPayStats?.success?.toLocaleString(), bg: "#f0fdf4", sub: `${scanPayStats?.successRate || 0}% rate` },
            { icon: <CurrencyRupeeIcon sx={{ color: "#ef5350", fontSize: 26 }} />, label: "Total Earnings", value: `₹${(scanPayStats?.totalEarn || 0).toLocaleString()}`, sub: `₹${(scanPayStats?.todayEarn || 0).toLocaleString()} today`, subColor: "#ef5350" },
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}><KpiCard {...c} /></Grid>
          ))}
        </Grid>

        {/* ── MONTHLY SCAN COUNT LINE + EARNINGS LINE ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Monthly Scan Payments vs Reports</SC>
              <Box sx={{ height: 280 }}>
                <Line data={monthlyLineData} options={chartOpts(theme)} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Monthly Earnings (₹)</SC>
              <Box sx={{ height: 200 }}>
                <Line data={earningLineData} options={chartOpts(theme)} />
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Grid container spacing={1.5}>
                {[
                  { label: "Total Earn", value: `₹${(scanPayStats?.totalEarn || 0).toLocaleString()}`, color: "#ef5350" },
                  { label: "Today Earn", value: `₹${(scanPayStats?.todayEarn || 0).toLocaleString()}`, color: INDIGO },
                  { label: "Success Rate", value: `${scanPayStats?.successRate || 0}%`, color: "#66bb6a" },
                  { label: "Scan Used %", value: `${scanPayStats?.scanUsedRate || 0}%`, color: "#26c6da" },
                ].map((s, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{ background: "#e8eaf6", borderRadius: 2, p: 1.2, textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
                      <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* ── SKIN TYPE BAR + PAY STATUS DOUGHNUT + GENDER DOUGHNUT + SCAN USED ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Skin Type Distribution</SC>
              <Box sx={{ height: 240 }}>
                <Bar data={skinTypeBarData} options={chartOpts(theme)} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={2.3}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Payment Status</SC>
              <Box sx={{ height: 160 }}>
                <Doughnut data={payStatusDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1 }} />
              {scanPayStatusBreakdown?.map((s, i) => (
                <Box key={i} display="flex" justifyContent="space-between" alignItems="center" py={0.3}>
                  <Typography variant="caption" fontWeight={600} textTransform="capitalize">{s._id}</Typography>
                  <Chip label={s.count} size="small" sx={{ fontWeight: 700, fontSize: 10 }} />
                </Box>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} md={2.3}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Gender Split</SC>
              <Box sx={{ height: 160 }}>
                <Doughnut data={genderDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1 }} />
              {genderKeys.map((k, i) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" py={0.3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: genderColors[i] }} />
                    <Typography variant="caption" fontWeight={600} textTransform="capitalize">{k}</Typography>
                  </Box>
                  <Chip label={scanGenderBreakdown[k]} size="small" sx={{ fontWeight: 700, fontSize: 10 }} />
                </Box>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Scan Usage</SC>
              <Box sx={{ height: 160 }}>
                <Doughnut data={scanUsedDoughnutData} options={doughnutOpts} />
              </Box>
              <Divider sx={{ my: 1 }} />
              {[["Used", scanPayStats?.scanUsed, INDIGO], ["Unused", scanPayStats?.scanUnused, "#e0e0e0"]].map(([k, v, c]) => (
                <Box key={k} display="flex" justifyContent="space-between" alignItems="center" py={0.3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: c }} />
                    <Typography variant="caption" fontWeight={600}>{k}</Typography>
                  </Box>
                  <Chip label={v || 0} size="small" sx={{ fontWeight: 700, fontSize: 10 }} />
                </Box>
              ))}
              <Box sx={{ mt: 1.5, background: "#e8eaf6", borderRadius: 2, p: 1, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">Used Rate</Typography>
                <Typography variant="h6" fontWeight={700} color={INDIGO}>{scanPayStats?.scanUsedRate || 0}%</Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* ── TOP CONCERNS BAR CHART + CONCERN PROGRESS TABLE ── */}
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Top Skin Concerns — Occurrences & Avg Severity %</SC>
              <Box sx={{ height: 300 }}>
                <Bar data={concernBarData} options={concernBarOpts} />
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, borderRadius: 3, height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
              <SC>Concern Frequency</SC>
              <Box sx={{ overflowY: "auto", maxHeight: 320 }}>
                {topScanConcerns?.map((c, i) => {
                  const pct = Math.min((c.count / maxConcernCount) * 100, 100);
                  return (
                    <Box key={i} mb={1.5}>
                      <Box display="flex" justifyContent="space-between" mb={0.4}>
                        <Typography variant="caption" fontWeight={600} noWrap sx={{ maxWidth: "65%" }}>{c._id || "—"}</Typography>
                        <Box display="flex" gap={0.8}>
                          <Chip label={`${c.count} scans`} size="small" sx={{ fontSize: 10, fontWeight: 700, background: "#e8eaf6", color: "#1a237e" }} />
                          <Chip label={`${c.avgPercentage.toFixed(1)}%`} size="small" sx={{ fontSize: 10, fontWeight: 700, background: "#fce4ec", color: "#c62828" }} />
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 7, borderRadius: 4,
                          "& .MuiLinearProgress-bar": {
                            background: pct > 70 ? "#ef5350" : pct > 40 ? "#ffa726" : INDIGO,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* ── LATEST 10 SCAN REPORTS ── */}
        <Card sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}>
          <SC>Latest Scan Reports (Last 10)</SC>
          <TableContainer>
            <Table sx={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "28%" }} />
              </colgroup>
              <TH cols={["#", "Name", "Gender", "Age", "Score", "Skin Type", "Concern", "Confidence", "Top Concerns"]} />
              <TableBody>
                {latestScanReports?.map((r, i) => {
                  const topConcerns = (r.concerns || []).slice(0, 3);
                  const scoreColor = !r.skinScore ? "#94a3b8" : r.skinScore >= 70 ? "#66bb6a" : r.skinScore >= 40 ? "#ffa726" : "#ef5350";
                  return (
                    <TableRow key={i} hover>
                      <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2" color="text.secondary">{i + 1}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 26, height: 26, fontSize: 11, flexShrink: 0, background: INDIGO }}>
                            {r.name?.[0] || "S"}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500} noWrap>{r.name || "—"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Chip label={r.gender || "—"} size="small" variant="outlined" sx={{ fontSize: 10, textTransform: "capitalize" }} />
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}><Typography variant="body2">{r.age || "—"}</Typography></TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box sx={{ background: `${scoreColor}18`, borderRadius: 1.5, px: 1, py: 0.3, display: "inline-block" }}>
                          <Typography variant="caption" fontWeight={800} color={scoreColor}>{r.skinScore ?? "—"}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Chip label={r.skinType || "—"} size="small" sx={{ fontSize: 10, background: "#e8eaf6", color: "#1a237e", fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" noWrap sx={{ fontSize: 11 }}>{r.skinTypeConcern || "—"}</Typography>
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        {r.skinTypeConfidence != null ? (
                          <Box>
                            <Typography variant="caption" fontWeight={700} color={INDIGO}>
                              {(r.skinTypeConfidence * 100).toFixed(0)}%
                            </Typography>
                            <LinearProgress variant="determinate" value={Math.min((r.skinTypeConfidence || 0) * 100, 100)}
                              sx={{ height: 4, borderRadius: 2, mt: 0.3, "& .MuiLinearProgress-bar": { background: INDIGO } }}
                            />
                          </Box>
                        ) : <Typography variant="body2">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ px: 2, py: 1 }}>
                        <Box display="flex" flexWrap="wrap" gap={0.4}>
                          {topConcerns.length > 0 ? topConcerns.map((c, ci) => (
                            <Chip key={ci}
                              label={`${c.name} ${c.percentage != null ? c.percentage + "%" : ""}`}
                              size="small"
                              sx={{
                                fontSize: 9, fontWeight: 600,
                                background: c.percentage > 60 ? "#fce4ec" : c.percentage > 30 ? "#fff3e0" : "#e8eaf6",
                                color: c.percentage > 60 ? "#c62828" : c.percentage > 30 ? "#e65100" : "#1a237e",
                              }}
                            />
                          )) : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default SkinAnalysisDashboard;
